import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import type { FaceToExpected } from '@/lib/npc-expected-synthesis';

/**
 * The pill of the column « Face à l'attendu » (npc.md §26.4, §26.5; issue #216, lot L): the verdict
 * IN WORDS - « aucun niveau en commun », « voisin : le Quoi tient », « pas de trouvé », « hors
 * base » - and never by its colour alone. A replay with nothing to say (no expected noyau on the
 * sheet, or no determination yet) carries a dash.
 *
 * The same pill stands in the table of the tab « Rejeux », in the grid under it and on the page of a
 * series, so the three say the same words in the same tones (`faceToExpected` words them).
 */

export interface NpcFacePillProps {
  readonly face: FaceToExpected;
  /** The mark a suite reads this pill by; `npc-face` on the three screens. */
  readonly testId?: string;
  /** « attendu actuel », « sans extrait »... under the pill, when the screen has the room. */
  readonly showNote?: boolean;
}

export function NpcFacePill({ face, testId = 'npc-face', showNote = false }: NpcFacePillProps) {
  const dash = face.kind === 'none' || face.kind === 'waiting';
  return (
    <span
      data-testid={testId}
      data-kind={face.kind}
      data-verdict={face.verdict ?? undefined}
      className="inline-flex flex-col items-start gap-0.5"
    >
      {dash ? (
        <span className="text-gray-700">
          <span aria-hidden="true">-</span>
          <span className="sr-only">
            {face.kind === 'none' ? 'pas de noyau attendu' : 'en attente de la détermination'}
          </span>
        </span>
      ) : face.tone === 'outline' ? (
        <span className="inline-flex items-center whitespace-nowrap rounded-full border border-gray-400 bg-white px-2.5 py-0.5 font-semibold text-caption text-gray-700">
          {face.label}
        </span>
      ) : (
        <StatusBadge tone={face.tone} size="caption">
          {face.label}
        </StatusBadge>
      )}
      {showNote && face.note !== null ? (
        <span data-testid="npc-face-note" className="block text-caption text-gray-700">
          {face.note}
        </span>
      ) : null}
    </span>
  );
}
